// The card (kit-sheet `.card`, screen-mock `.card`): dark frame, name plate,
// art window with the orange cog-arc divider, rarity as a coloured glow.
//
// **The five rarity colours are used here and nowhere else** (PLAN-UI §28.2).
// A rarity hue borrowed as a general accent stops meaning rarity, which is the
// one thing it is for — so `theme.kit.rarity` has exactly one reader and this
// is it.
//
// **The fan is not here.** Hover lift, rotation and the neighbours sliding
// apart belong to the hand container (U7): a card in a celebration modal or a
// costs table must not lurch when the pointer crosses it. What stays is the
// base transition and `transform-origin: 50% 120%`, so the parent can drive
// the whole thing without re-declaring either.

import { styled } from "@mui/material/styles";

import { motion } from "./motion";
import { TOKENS } from "./tokens";

export type Rarity = "common" | "uncommon" | "rare" | "epic" | "legend";

export const GameCard = styled("div", {
  shouldForwardProp: (prop: string) => prop !== "rarity"
})<{ rarity?: Rarity }>(({ theme, rarity = "common" }) => {
  const glow = theme.kit.rarity[rarity];
  return {
    position: "relative",
    width: 138,
    height: 196,
    padding: 5,
    background: theme.kit.color.panelRaised,
    border: `2.5px solid ${theme.kit.color.line}`,
    borderRadius: theme.kit.radius.card,
    color: theme.kit.color.ink,
    fontFamily: theme.kit.font.body,
    cursor: "pointer",
    transformOrigin: "50% 120%",
    // **The rarity ring first, so it is painted on top.** Box-shadows paint
    // in order with the first uppermost, and with the plate's 4px drop listed
    // first it covered the ring along the card's foot — the glow stopped short
    // of the bottom edge (designer, U7 review). The drop now sits *under* the
    // ring and is offset by the ring's own width as well, so 4px of it still
    // shows beneath the coloured outline rather than none.
    boxShadow: [
      `0 0 0 2.5px ${glow}`,
      `0 6.5px 0 ${theme.kit.color.shadow}`,
      `0 0 14px color-mix(in srgb, ${glow} 55%, transparent)`
    ].join(", "),
    ...motion(["transform"], [], TOKENS.motion.duration.draw)
  };
});

/** The name plate — a dark inset above the art. */
export const CardPlate = styled("div")(({ theme }) => ({
  background: `color-mix(in srgb, ${theme.kit.color.panel} 20%, ${theme.kit.color.ground})`,
  border: `1.5px solid ${theme.kit.color.line}`,
  borderRadius: theme.kit.radius.sm,
  fontFamily: theme.kit.font.heading,
  fontWeight: 400,
  fontSize: 12.5,
  letterSpacing: ".4px",
  textAlign: "center",
  padding: "5px 4px 4px"
}));

/** The art window, with the brand cog-arc rising out of its floor. */
export const CardArt = styled("div")(({ theme }) => ({
  position: "relative",
  overflow: "hidden",
  height: 84,
  margin: "5px 0",
  borderRadius: theme.kit.radius.sm,
  background: `radial-gradient(ellipse at 50% 30%, color-mix(in srgb, ${theme.kit.color.ink} 8%, ${theme.kit.color.lineHi}), color-mix(in srgb, ${theme.kit.color.line} 60%, ${theme.kit.color.panelRaised}) 75%)`,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  fontSize: 40,

  "&::after": {
    content: '""',
    position: "absolute",
    left: "-8%",
    right: "-8%",
    bottom: -3,
    height: 12,
    background: theme.kit.color.brand,
    borderRadius: "50% 50% 0 0 / 100% 100% 0 0",
    boxShadow: `0 -2px 0 ${theme.kit.color.brandDown} inset`
  }
}));

/** What the card does, in a sentence. */
export const CardText = styled("div")(({ theme }) => ({
  fontSize: 10.8,
  lineHeight: 1.35,
  color: theme.kit.color.inkDim,
  padding: "2px 4px"
}));

/** What it costs — "free", "turn" — hung off the top-right corner. */
export const CardCost = styled("span")(({ theme }) => ({
  position: "absolute",
  top: -10,
  right: -8,
  background: theme.kit.color.brand,
  color: theme.kit.color.onBrand,
  border: `2.5px solid ${theme.kit.color.brandDown}`,
  borderRadius: theme.kit.radius.pill,
  padding: "2px 8px",
  fontFamily: theme.kit.font.display,
  fontWeight: 700,
  fontSize: 10
}));
